import millisecondsToSeconds from 'date-fns/millisecondsToSeconds';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnmount } from 'react-use';

import { Waveform } from '@eluve/blocks';
import { Icon, Stack, Text } from '@eluve/components';
import { formatSecondsToTime } from '@eluve/date-utils';
import { useAppointmentTotalDuration } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { AUDIO_LEVEL_ANALYSER_SETTINGS } from '@eluve/mic-utils';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { recordingDurationStore } from '@eluve/stores';
import { useTotalAudioDurationFromLocalFiles } from '@eluve/user-local-files';

import { useTranscriptionMachineSelector } from '../hooks/useTranscriptionMachine';

const WAVEFORM_BAR_COUNT = 12;
const WAVEFORM_UPDATE_INTERVAL_MS = 100;

interface WaveformDisplayProps {
  data: number[];
  duration: number;
  loading?: boolean;
}

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({
  data,
  duration,
  loading = false,
}) => (
  <Stack
    className="rounded-lg"
    direction="horizontal"
    fullWidth={false}
    gap={2}
  >
    <Stack
      className={
        loading
          ? 'rounded-lg bg-border-secondary/40 p-2 text-text-supporting sm:gap-1.5 md:gap-2'
          : 'rounded-lg bg-border-accent-primary/40 p-2 text-border-accent-quaternary sm:gap-1.5 md:gap-2'
      }
      fullWidth={false}
      align="center"
      gap={1}
    >
      <Icon name="Mic" size="xl" className="size-4 md:size-5" />
    </Stack>
    <span className="font-mono text-sm">{formatSecondsToTime(duration)}</span>
    <Waveform data={data} barCount={WAVEFORM_BAR_COUNT} />
  </Stack>
);

interface WaveformWhenRecordingProps {
  totalDuration: number;
  onTimeDurationUpdate?: (time: number) => void;
}

const WaveformWhenRecording: React.FC<WaveformWhenRecordingProps> = ({
  totalDuration,
  onTimeDurationUpdate,
}) => {
  const waveformAnalyserNode = useTranscriptionMachineSelector(
    (state) => state.context.waveformAnalyserNode,
  );

  // Fallback for blob audio source path (no shared AnalyserNode available)
  const clonedStream = useRef<MediaStream | null>(null);
  const mediaStream = useTranscriptionMachineSelector((state) => {
    if (waveformAnalyserNode) return null;

    const { mediaStream } = state.context;
    if (
      mediaStream &&
      mediaStream.active &&
      (!clonedStream.current || !clonedStream.current.active)
    ) {
      clonedStream.current = mediaStream.clone();
    }
    return clonedStream.current;
  });

  useUnmount(() => {
    clonedStream.current?.getTracks().forEach((track) => track.stop());
  });

  const ownedContextRef = useRef<AudioContext | null>(null);
  const [bars, setBars] = useState<number[]>([]);
  const [elapsedTime, setElapsedTime] = useState<number>(totalDuration);

  useEffect(() => {
    setElapsedTime(totalDuration);
  }, [totalDuration]);

  useEffect(() => {
    let analyser: AnalyserNode;

    if (waveformAnalyserNode) {
      analyser = waveformAnalyserNode;
    } else if (mediaStream) {
      const audioContext = new AudioContext();
      ownedContextRef.current = audioContext;
      analyser = audioContext.createAnalyser();
      analyser.fftSize = AUDIO_LEVEL_ANALYSER_SETTINGS.fftSize;
      analyser.smoothingTimeConstant =
        AUDIO_LEVEL_ANALYSER_SETTINGS.smoothingTimeConstant;
      analyser.minDecibels = AUDIO_LEVEL_ANALYSER_SETTINGS.minDecibels;
      analyser.maxDecibels = AUDIO_LEVEL_ANALYSER_SETTINGS.maxDecibels;
      const source = audioContext.createMediaStreamSource(mediaStream);
      source.connect(analyser);
    } else {
      return;
    }

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (analyser.context.state === 'closed') {
        return;
      }
      analyser.getByteFrequencyData(dataArray);
      setBars(Array.from(dataArray));
    };

    draw();
    const intervalId = window.setInterval(draw, WAVEFORM_UPDATE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      if (ownedContextRef.current) {
        ownedContextRef.current.close();
        ownedContextRef.current = null;
      }
    };
  }, [waveformAnalyserNode, mediaStream]);

  useEffect(() => {
    if (!waveformAnalyserNode && !mediaStream) return;

    const timerInterval = setInterval(() => {
      setElapsedTime((prevTime) => prevTime + 1);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [waveformAnalyserNode, mediaStream]);

  useEffect(() => {
    onTimeDurationUpdate?.(elapsedTime);
  }, [elapsedTime, onTimeDurationUpdate]);

  if (!waveformAnalyserNode && !mediaStream) {
    return <WaveformDisplay data={[]} duration={elapsedTime} loading />;
  }

  return <WaveformDisplay data={bars} duration={elapsedTime} />;
};

export const TranscriptionWaveform: React.FC = () => {
  const { t } = useTranslation();
  const [recordingDuration, setRecordingDuration] = useState<number>(0);

  const isRecording = useTranscriptionMachineSelector((state) =>
    state.matches({
      InSession: 'Recording',
    }),
  );

  const isMuted = useTranscriptionMachineSelector(
    (state) => state.context.microphoneStatus === 'muted',
  );
  const logger = useNamedLogger('TranscriptionWaveform');
  const tenantId = useTenantIdFromParams() ?? '';
  const { appointmentId: appointmentIdFromParams } = useParams<{
    appointmentId?: string;
  }>();

  const appointmentId =
    useTranscriptionMachineSelector((s) => s.context.sessionAppointmentId) ??
    appointmentIdFromParams ??
    '';

  const persistedTotalDuration = useAppointmentTotalDuration(appointmentId);
  const totalDurationFromLocalFiles = useTotalAudioDurationFromLocalFiles(
    tenantId,
    appointmentId,
    logger,
  );

  useEffect(() => {
    setRecordingDuration(
      millisecondsToSeconds(
        persistedTotalDuration + totalDurationFromLocalFiles,
      ),
    );
  }, [persistedTotalDuration, totalDurationFromLocalFiles]);

  // Publish the displayed duration as the single source the short-recording
  // guard reads, so the guard can never disagree with the on-screen timer.
  useEffect(() => {
    recordingDurationStore.send({
      type: 'setRecordingDuration',
      appointmentId: appointmentId || null,
      durationSeconds: recordingDuration,
    });
  }, [appointmentId, recordingDuration]);

  if (!isRecording) {
    return (
      <div>
        <Stack
          className="rounded-lg p-2"
          direction="horizontal"
          fullWidth={false}
          gap={6}
        >
          <span className="font-mono text-sm">
            {formatSecondsToTime(recordingDuration)}
          </span>
        </Stack>
      </div>
    );
  }

  if (isMuted) {
    return (
      <Stack
        className="rounded-lg p-2 pr-4"
        direction="horizontal"
        fullWidth={false}
        gap={4}
      >
        <div className="rounded-lg bg-bg-negative-subtle p-2 text-text-negative-subtle">
          <Icon name="MicOff" size="lg" />
        </div>
        <Text>{t('appointmentPage.waveform.muted')}</Text>
      </Stack>
    );
  }

  return (
    <WaveformWhenRecording
      totalDuration={recordingDuration}
      onTimeDurationUpdate={setRecordingDuration}
    />
  );
};
